import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery,
  CssBaseline,
  Paper,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  DescriptionOutlined as DocumentIcon,
  HomeOutlined as HomeIcon,
  CalendarMonth as CalendarMonthIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon,
  EventAvailable as EventAvailableIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  VideocamOutlined as VideocamOutlinedIcon,
  PersonOutlined as PersonOutlinedIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { consultasIniciais, pacientesIniciais } from '../shared/initialData';
import AgendaPaciente from './AgendaPaciente';

const drawerWidth = 280;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      padding: theme.spacing(2),
    },
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const Dashboard = () => {
  const [selectedTab, setSelectedTab] = useState('inicio');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pacienteId, setPacienteId] = useState('1');
  const [nomePaciente, setNomePaciente] = useState('');
  const [iniciais, setIniciais] = useState('');
  const [paciente, setPaciente] = useState(null);
  const [consultas, setConsultas] = useState([]);
  const [openNovaConsultaDialog, setOpenNovaConsultaDialog] = useState(false);
  const [novaConsultaData, setNovaConsultaData] = useState({
    data: '',
    horario: '',
    tipo: 'Presencial',
    observacoes: ''
  });
  const [novaConsultaErros, setNovaConsultaErros] = useState({});
  const [openReagendamentoDialog, setOpenReagendamentoDialog] = useState(false);
  const [reagendamentoData, setReagendamentoData] = useState({
    consultaId: null,
    dataAtual: '',
    horarioAtual: '',
    data: '',
    horario: '',
    motivo: ''
  });
  const [reagendamentoErros, setReagendamentoErros] = useState({});
  const [viewConsultaDialog, setViewConsultaDialog] = useState(false);
  const [selectedConsulta, setSelectedConsulta] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [showImageActions, setShowImageActions] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Verificar se o usuário é paciente e obter seus dados
  useEffect(() => {
    const tipoUsuario = localStorage.getItem('userType');
    
    if (tipoUsuario !== 'paciente') {
      // Redirecionar para a página de login se não for paciente
      window.location.href = '/login';
      return;
    }
    
    // Obter o ID do paciente do localStorage
    const storedPacienteId = localStorage.getItem('userId');
    if (storedPacienteId) {
      setPacienteId(storedPacienteId);
    }
    
    // Obter os dados do paciente
    const email = localStorage.getItem('email');
    const pacienteData = pacientesIniciais.find(p => p.email === email) || 
                        pacientesIniciais.find(p => p.id === parseInt(storedPacienteId));
    
    if (pacienteData) {
      setPaciente(pacienteData);
      setNomePaciente(pacienteData.nome);
      
      // Extrair iniciais do nome
      const palavras = pacienteData.nome.split(' ');
      if (palavras.length >= 2) {
        setIniciais(`${palavras[0][0]}${palavras[1][0]}`);
      } else if (palavras.length === 1) {
        setIniciais(palavras[0][0]);
      } else {
        setIniciais('PC');
      }
    }
    
    // Carregar consultas do paciente
    const pacienteConsultas = consultasIniciais.filter(c => 
      c.pacienteId === parseInt(storedPacienteId)
    );
    setConsultas(pacienteConsultas);
    
    // Em telas grandes, o drawer fica aberto por padrão
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userType');
    localStorage.removeItem('userId');
    localStorage.removeItem('email');
    window.location.href = '/login';
  };

  const handleOpenNovaConsultaDialog = () => {
    setNovaConsultaData({
      data: '',
      horario: '',
      tipo: 'Presencial',
      observacoes: ''
    });
    setNovaConsultaErros({});
    setOpenNovaConsultaDialog(true);
  };
  
  const handleCloseNovaConsultaDialog = () => {
    setOpenNovaConsultaDialog(false);
  };
  
  const handleNovaConsultaChange = (field, value) => {
    setNovaConsultaData({
      ...novaConsultaData,
      [field]: value
    });
    
    // Limpar erro quando o usuário corrige o campo
    if (novaConsultaErros[field]) {
      setNovaConsultaErros({
        ...novaConsultaErros,
        [field]: null
      });
    }
  };
  
  const validateNovaConsultaForm = () => {
    const erros = {};
    
    if (!novaConsultaData.data) erros.data = 'Selecione uma data';
    if (!novaConsultaData.horario) erros.horario = 'Selecione um horário';
    
    setNovaConsultaErros(erros);
    return Object.keys(erros).length === 0;
  };
  
  const handleSubmitNovaConsulta = () => {
    if (!validateNovaConsultaForm()) return;
    
    // Aqui seria implementada a lógica para enviar a solicitação ao backend
    // Por enquanto, vamos apenas simular com um alert
    alert(`Solicitação de nova consulta enviada ao psicólogo.\n\nData: ${novaConsultaData.data}\nHorário: ${novaConsultaData.horario}\nTipo: ${novaConsultaData.tipo}`);
    
    handleCloseNovaConsultaDialog();
  };

  const handleOpenReagendamentoDialog = (consulta) => {
    setReagendamentoData({
      consultaId: consulta.id,
      dataAtual: consulta.data,
      horarioAtual: consulta.horario,
      data: '',
      horario: '',
      motivo: ''
    });
    setReagendamentoErros({});
    setOpenReagendamentoDialog(true);
  };
  
  const handleCloseReagendamentoDialog = () => {
    setOpenReagendamentoDialog(false);
  };
  
  const handleReagendamentoChange = (field, value) => {
    setReagendamentoData({
      ...reagendamentoData,
      [field]: value
    });
    
    // Limpar erro quando o usuário corrige o campo
    if (reagendamentoErros[field]) {
      setReagendamentoErros({
        ...reagendamentoErros,
        [field]: null
      });
    }
  };
  
  const validateReagendamentoForm = () => {
    const erros = {};
    
    if (!reagendamentoData.data) erros.data = 'Selecione uma data';
    if (!reagendamentoData.horario) erros.horario = 'Selecione um horário';
    
    setReagendamentoErros(erros);
    return Object.keys(erros).length === 0;
  };
  
  const handleSubmitReagendamento = () => {
    if (!validateReagendamentoForm()) return;
    
    // Aqui seria implementada a lógica para enviar a solicitação ao backend
    // Por enquanto, vamos apenas simular com um alert
    alert(`Solicitação de reagendamento enviada ao psicólogo.\n\nConsulta: ${reagendamentoData.dataAtual} às ${reagendamentoData.horarioAtual}\nNova data: ${reagendamentoData.data}\nNovo horário: ${reagendamentoData.horario}\nMotivo: ${reagendamentoData.motivo || 'Não informado'}`);
    
    handleCloseReagendamentoDialog();
  };

  const handleViewConsulta = (consulta) => {
    setSelectedConsulta(consulta);
    setViewConsultaDialog(true);
  };
  
  const handleCloseViewConsultaDialog = () => {
    setViewConsultaDialog(false);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleRemoveImage = () => {
    setProfileImage(null);
  };

  const menuItems = [
    {
      id: 'inicio',
      label: 'Início',
      icon: <HomeIcon />
    },
    {
      id: 'consultas',
      label: 'Minhas Consultas',
      icon: <EventIcon />
    },
    {
      id: 'perfil',
      label: 'Meus Dados',
      icon: <PersonIcon />
    },
    {
      id: 'documentos',
      label: 'Documentos',
      icon: <DocumentIcon />
    },
    {
      id: 'agenda',
      label: 'Minha Agenda',
      icon: <CalendarMonthIcon />
    }
  ];

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'Realizada':
        return { bg: '#4caf50', color: 'white' };
      case 'Agendada':
        return { bg: '#2196f3', color: 'white' };
      case 'Cancelada':
        return { bg: '#f44336', color: 'white' };
      default:
        return { bg: '#757575', color: 'white' };
    }
  };

  const renderHomeContent = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Bem-vindo(a), {nomePaciente}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Typography variant="h6">Próxima Consulta</Typography>
              <CalendarMonthIcon sx={{ color: '#4a8a84' }} />
            </Box>
            {consultas.length > 0 ? (
              <>
                <Typography variant="body1" gutterBottom>
                  <strong>{consultas[0].data}</strong> às <strong>{consultas[0].horario}</strong>
                </Typography>
                <Box display="flex" alignItems="center" mb={1}>
                  <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {consultas[0].tipo === 'Presencial' ? 'Dra. Rafaela Mendes Luz' : 'Dr. João Silva'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mb={1}>
                  {consultas[0].tipo === 'Presencial' ? (
                    <LocationOnOutlinedIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  ) : (
                    <VideocamOutlinedIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  )}
                  <Typography variant="body2">
                    {consultas[0].tipo}
                    {consultas[0].tipo === 'Online' && consultas[0].plataforma && ` (${consultas[0].plataforma})`}
                  </Typography>
                </Box>
                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => handleViewConsulta(consultas[0])}
                    sx={{ 
                      borderColor: '#4a8a84', 
                      color: '#4a8a84',
                      '&:hover': { borderColor: '#4a8a84', bgcolor: 'rgba(74, 138, 132, 0.08)' }
                    }}
                  >
                    Ver Detalhes
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Você não tem consultas agendadas
                </Typography>
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={handleOpenNovaConsultaDialog}
                  sx={{ 
                    mt: 1,
                    bgcolor: '#4a8a84', 
                    '&:hover': { bgcolor: '#3a7a74' }
                  }}
                >
                  Agendar Consulta
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper 
            sx={{ 
              p: 3, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              bgcolor: 'rgba(74, 138, 132, 0.08)'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Typography variant="h6">Agendar Nova Consulta</Typography>
              <EventAvailableIcon sx={{ color: '#4a8a84' }} />
            </Box>
            <Typography variant="body2" paragraph>
              Agende facilmente uma nova consulta com seu psicólogo.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Selecione a data e horário de sua preferência. Sua solicitação será analisada pelo psicólogo.
            </Typography>
            <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                size="large"
                onClick={handleOpenNovaConsultaDialog}
                sx={{ 
                  minWidth: 200,
                  bgcolor: '#4a8a84', 
                  '&:hover': { bgcolor: '#3a7a74' }
                }}
              >
                Solicitar Agendamento
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Próximas Consultas
              </Typography>
              <TableContainer>
                <Table aria-label="tabela de próximas consultas">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Horário</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consultas
                      .filter(c => c.status === 'Agendada')
                      .sort((a, b) => {
                        const dateA = new Date(a.data.split('/').reverse().join('-'));
                        const dateB = new Date(b.data.split('/').reverse().join('-'));
                        return dateA - dateB;
                      })
                      .slice(0, 3)
                      .map((consulta) => (
                        <TableRow key={consulta.id}>
                          <TableCell>{consulta.data}</TableCell>
                          <TableCell>{consulta.horario}</TableCell>
                          <TableCell>{consulta.tipo}</TableCell>
                          <TableCell>
                            <Chip 
                              label={consulta.status} 
                              size="small"
                              sx={{ 
                                bgcolor: getStatusChipColor(consulta.status).bg,
                                color: getStatusChipColor(consulta.status).color
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    {consultas.filter(c => c.status === 'Agendada').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Não há consultas agendadas no momento
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Últimos Acompanhamentos
              </Typography>
              {consultas
                .filter(c => c.status === 'Realizada' && c.acompanhamentos && c.acompanhamentos.length > 0)
                .slice(0, 2)
                .map((consulta) => (
                  <Box key={consulta.id} sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Consulta em {consulta.data}
                    </Typography>
                    {consulta.acompanhamentos.map((acomp) => (
                      <Box key={acomp.id} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {acomp.data}
                        </Typography>
                        <Typography variant="body2">
                          {acomp.conteudo}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              {consultas.filter(c => c.status === 'Realizada' && c.acompanhamentos && c.acompanhamentos.length > 0).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Não há registros de acompanhamentos disponíveis
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Informações do Psicólogo
              </Typography>
              
              {paciente && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#4a8a84', mr: 2 }}>
                      {paciente.psicologoId === 1 ? 'RS' : 'JS'}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1">
                        {paciente.psicologoId === 1 ? 'Dra. Rafaela Mendes Luz' : 'Dr. João Silva'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {paciente.psicologoId === 1 ? 'CRP: 06/123456' : 'CRP: 06/654321'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Especialidade:</strong> {paciente.psicologoId === 1 ? 'Psicologia Clínica' : 'Psicologia Clínica e Organizacional'}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Contato:</strong> {paciente.psicologoId === 1 ? '(11) 98765-4321' : '(11) 98712-3456'}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Documentos Recentes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Não há documentos disponíveis no momento
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Dialog 
        open={openNovaConsultaDialog}
        onClose={handleCloseNovaConsultaDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#4a8a84', color: 'white' }}>
          Solicitar Nova Consulta
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Para solicitar uma nova consulta, preencha os dados abaixo com sua preferência de data e horário. Sua solicitação será enviada ao psicólogo para aprovação.
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Data"
                  type="date"
                  value={novaConsultaData.data}
                  onChange={(e) => handleNovaConsultaChange('data', e.target.value)}
                  error={!!novaConsultaErros.data}
                  helperText={novaConsultaErros.data}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Horário"
                  type="time"
                  value={novaConsultaData.horario}
                  onChange={(e) => handleNovaConsultaChange('horario', e.target.value)}
                  error={!!novaConsultaErros.horario}
                  helperText={novaConsultaErros.horario}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Consulta</InputLabel>
                  <Select
                    value={novaConsultaData.tipo}
                    label="Tipo de Consulta"
                    onChange={(e) => handleNovaConsultaChange('tipo', e.target.value)}
                  >
                    <MenuItem value="Presencial">Presencial</MenuItem>
                    <MenuItem value="Online">Online</MenuItem>
                    <MenuItem value="Domiciliar">Domiciliar</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observações (opcional)"
                  multiline
                  rows={3}
                  value={novaConsultaData.observacoes}
                  onChange={(e) => handleNovaConsultaChange('observacoes', e.target.value)}
                  placeholder="Informe qualquer detalhe importante sobre a consulta..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNovaConsultaDialog}>Cancelar</Button>
          <Button 
            variant="contained" 
            sx={{ bgcolor: '#4a8a84', '&:hover': { bgcolor: '#3a7a74' } }}
            onClick={handleSubmitNovaConsulta}
          >
            Enviar Solicitação
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const renderConsultasContent = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Minhas Consultas
      </Typography>
      
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table aria-label="tabela de consultas">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Horário</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Plataforma</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Observações</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consultas
                .sort((a, b) => {
                  const dateA = new Date(a.data.split('/').reverse().join('-'));
                  const dateB = new Date(b.data.split('/').reverse().join('-'));
                  return dateB - dateA; // Ordenação decrescente
                })
                .map((consulta) => (
                  <TableRow key={consulta.id}>
                    <TableCell>{consulta.data}</TableCell>
                    <TableCell>{consulta.horario}</TableCell>
                    <TableCell>{consulta.tipo}</TableCell>
                    <TableCell>{consulta.plataforma}</TableCell>
                    <TableCell>
                      <Chip 
                        label={consulta.status} 
                        size="small"
                        sx={{ 
                          bgcolor: getStatusChipColor(consulta.status).bg,
                          color: getStatusChipColor(consulta.status).color
                        }}
                      />
                    </TableCell>
                    <TableCell>{consulta.observacoes.substring(0, 50)}{consulta.observacoes.length > 50 ? '...' : ''}</TableCell>
                    <TableCell>
                      {consulta.status === 'Agendada' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            size="small" 
                            color="warning"
                            onClick={() => handleOpenReagendamentoDialog(consulta)}
                            title="Solicitar reagendamento"
                          >
                            <HourglassEmptyIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => alert('Solicitação de cancelamento enviada ao psicólogo.')}
                            title="Cancelar consulta"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              {consultas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Não há consultas registradas
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Dialog para reagendamento */}
      <Dialog 
        open={openReagendamentoDialog} 
        onClose={handleCloseReagendamentoDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#ff9800', color: 'white' }}>
          Solicitar Reagendamento
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Para solicitar o reagendamento da consulta, preencha os dados abaixo com a nova data e horário desejados. Sua solicitação será enviada ao psicólogo para aprovação.
            </Typography>
            
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Consulta atual
              </Typography>
              <Typography variant="body2">
                <strong>Data:</strong> {reagendamentoData.dataAtual}
              </Typography>
              <Typography variant="body2">
                <strong>Horário:</strong> {reagendamentoData.horarioAtual}
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nova Data"
                  type="date"
                  value={reagendamentoData.data}
                  onChange={(e) => handleReagendamentoChange('data', e.target.value)}
                  error={!!reagendamentoErros.data}
                  helperText={reagendamentoErros.data}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Novo Horário"
                  type="time"
                  value={reagendamentoData.horario}
                  onChange={(e) => handleReagendamentoChange('horario', e.target.value)}
                  error={!!reagendamentoErros.horario}
                  helperText={reagendamentoErros.horario}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Motivo do Reagendamento (opcional)"
                  multiline
                  rows={3}
                  value={reagendamentoData.motivo}
                  onChange={(e) => handleReagendamentoChange('motivo', e.target.value)}
                  placeholder="Informe brevemente o motivo do reagendamento..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReagendamentoDialog}>Cancelar</Button>
          <Button 
            variant="contained" 
            color="warning"
            sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
            onClick={handleSubmitReagendamento}
          >
            Enviar Solicitação
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const renderPerfilContent = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Meus Dados
      </Typography>
      
      {paciente && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', mb: 4, gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box 
                sx={{ 
                  position: 'relative',
                  '&:hover': {
                    '& .image-actions': {
                      opacity: 1
                    }
                  }
                }}
                onMouseEnter={() => setShowImageActions(true)}
                onMouseLeave={() => setShowImageActions(false)}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <Tooltip title="Alterar foto">
                      <IconButton 
                        sx={{ 
                          bgcolor: '#4a8a84', 
                          color: 'white',
                          '&:hover': { bgcolor: '#3a7a74' }
                        }}
                        component="label"
                      >
                        <EditIcon fontSize="small" />
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <Avatar 
                    src={profileImage}
                    sx={{ 
                      width: 150, 
                      height: 150,
                      fontSize: '3rem',
                      bgcolor: '#4a8a84'
                    }}
                  >
                    {iniciais}
                  </Avatar>
                </Badge>
                
                {profileImage && showImageActions && (
                  <Box 
                    className="image-actions"
                    sx={{ 
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      p: 1,
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      bgcolor: 'rgba(0,0,0,0.6)',
                      borderRadius: '0 0 50% 50%'
                    }}
                  >
                    <IconButton 
                      size="small" 
                      onClick={handleRemoveImage}
                      sx={{ color: 'white' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                {!profileImage && (
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    component="label"
                    sx={{ 
                      mt: 1,
                      borderColor: '#4a8a84', 
                      color: '#4a8a84',
                      '&:hover': { borderColor: '#3a7a74', bgcolor: 'rgba(74, 138, 132, 0.08)' }
                    }}
                  >
                    Carregar Foto
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </Button>
                )}
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Tamanho máximo de 5MB
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome Completo"
                    value={paciente.nome}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={paciente.email}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Telefone"
                    value={paciente.telefone}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Data de Nascimento"
                    value={paciente.dataNascimento}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Gênero"
                    value={paciente.genero}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Estado Civil"
                    value={paciente.estadoCivil}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Profissão"
                value={paciente.profissao}
                InputProps={{ readOnly: true }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Histórico Clínico
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">
                  {paciente.historico}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );

  const renderDocumentosContent = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Documentos
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" gutterBottom>
          Nesta área você pode acessar documentos e relatórios compartilhados pelo seu psicólogo.
        </Typography>
        
        <Box sx={{ mt: 3 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label="Relatório" 
                      sx={{ bgcolor: '#4a8a84', color: 'white' }} 
                    />
                  </TableCell>
                  <TableCell>Relatório de Acompanhamento Psicológico</TableCell>
                  <TableCell>10/03/2024</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" sx={{ color: '#4a8a84' }}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ color: '#4a8a84' }}>
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label="Declaração" 
                      sx={{ bgcolor: '#ff9800', color: 'white' }} 
                    />
                  </TableCell>
                  <TableCell>Declaração de Comparecimento</TableCell>
                  <TableCell>25/02/2024</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" sx={{ color: '#4a8a84' }}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ color: '#4a8a84' }}>
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label="Atestado" 
                      sx={{ bgcolor: '#f44336', color: 'white' }} 
                    />
                  </TableCell>
                  <TableCell>Atestado Psicológico</TableCell>
                  <TableCell>15/02/2024</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" sx={{ color: '#4a8a84' }}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ color: '#4a8a84' }}>
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
          Solicitações
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Para solicitar um novo documento, entre em contato diretamente com seu psicólogo.
        </Typography>
      </Paper>
    </Box>
  );

  const renderContent = () => {
    switch (selectedTab) {
      case 'inicio':
        return renderHomeContent();
      case 'consultas':
        return renderConsultasContent();
      case 'perfil':
        return renderPerfilContent();
      case 'documentos':
        return renderDocumentosContent();
      case 'agenda':
        return <AgendaPaciente />;
      default:
        return renderHomeContent();
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#4a8a84'
      }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Área do Paciente
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'secondary.main', marginRight: 1 }}>
              {iniciais}
            </Avatar>
            <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
              {nomePaciente}
            </Typography>
            <IconButton color="inherit" onClick={handleLogout}>
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Drawer de navegação */}
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        ModalProps={{
          keepMounted: true // Melhor desempenho em dispositivos móveis
        }}
      >
        <DrawerHeader>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexGrow: 1, 
            ml: 2,
            gap: 2
          }}>
            <Avatar sx={{ bgcolor: '#4a8a84' }}>{iniciais}</Avatar>
            <Typography variant="h6" color="text.primary">
              {nomePaciente}
            </Typography>
          </Box>
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        
        <Divider />
        
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                selected={selectedTab === item.id}
                onClick={() => handleTabChange(item.id)}
                sx={{
                  py: 1.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(74, 138, 132, 0.1)',
                    borderLeft: '4px solid #4a8a84',
                    '&:hover': {
                      backgroundColor: 'rgba(74, 138, 132, 0.2)',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(74, 138, 132, 0.1)',
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: selectedTab === item.id ? '#4a8a84' : 'inherit'
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label} 
                  primaryTypographyProps={{
                    fontWeight: selectedTab === item.id ? 'medium' : 'regular',
                    color: selectedTab === item.id ? '#4a8a84' : 'inherit'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        <Divider />
        
        <Box sx={{ mt: 'auto', p: 2 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Sair" />
            </ListItemButton>
          </ListItem>
        </Box>
      </Drawer>
      
      <Main open={drawerOpen && !isMobile}>
        <DrawerHeader />
        {renderContent()}
      </Main>
      
      {/* Diálogo para visualizar detalhes da consulta */}
      <Dialog
        open={viewConsultaDialog}
        onClose={handleCloseViewConsultaDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#4a8a84', color: 'white' }}>
          Detalhes da Consulta
        </DialogTitle>
        <DialogContent>
          {selectedConsulta && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Data
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedConsulta.data}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Horário
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedConsulta.horario}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tipo
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedConsulta.tipo}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip 
                    label={selectedConsulta.status} 
                    size="small"
                    sx={{ 
                      bgcolor: getStatusChipColor(selectedConsulta.status).bg,
                      color: getStatusChipColor(selectedConsulta.status).color
                    }}
                  />
                </Grid>
                
                {selectedConsulta.tipo === 'Online' && selectedConsulta.plataforma && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Plataforma
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedConsulta.plataforma}
                    </Typography>
                  </Grid>
                )}
                
                {selectedConsulta.observacoes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Observações
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedConsulta.observacoes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              
              {selectedConsulta.status === 'Agendada' && (
                <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<HourglassEmptyIcon />}
                    onClick={() => {
                      handleCloseViewConsultaDialog();
                      handleOpenReagendamentoDialog(selectedConsulta);
                    }}
                  >
                    Solicitar Reagendamento
                  </Button>
                  
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      handleCloseViewConsultaDialog();
                      alert('Solicitação de cancelamento enviada ao psicólogo.');
                    }}
                  >
                    Cancelar Consulta
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewConsultaDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard; 